import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { goBack, navigate, showToast, friendlyError, bumpListsVersion } from '../store.js';
import { __ } from '../i18n.js';
import Header from '../components/Header.jsx';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';
import Modal from '../components/Modal.jsx';
import ListEditModal from '../components/ListEditModal.jsx';
import ItemAddInput from '../components/ItemAddInput.jsx';
import {
  getListStore,
  refreshListStore,
  dropListStore,
  checkItem,
  addItem,
  isDirty,
} from '../lib/shoplist-sync.js';
import { SHOPLIST_CATEGORIES, SHOPLIST_UNCATEGORIZED, shoplistCategoryInfo } from '../lib/shoplist-categories.js';

/**
 * "Vaste lijst toevoegen" (plan §4.2): a picker over the household's
 * templates (one tap even when there's only one — no special-cased
 * shortcut, matching "one form, no wizard" simplicity elsewhere in this
 * codebase). Reports what happened ("3 toegevoegd, 2 stonden al op de
 * lijst") rather than a bare success toast, since "skipped" is a
 * meaningful outcome here, not a failure.
 */
function PourTemplateModal({ listId, templates, onClose, onPoured }) {
  const [pouring, setPouring] = useState(false);

  async function handlePour(templateId) {
    if (pouring) return;
    setPouring(true);
    try {
      const res = await api.lists.pour(listId, templateId);
      const parts = [];
      if (res.added > 0) parts.push(__('%d toegevoegd', 'etm').replace('%d', res.added));
      if (res.skipped > 0) parts.push(__('%d stond al op de lijst', 'etm').replace('%d', res.skipped));
      showToast(parts.length ? parts.join(', ') : __('Niets om toe te voegen.', 'etm'), 'success');
      onPoured();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setPouring(false);
    }
  }

  return (
    <Modal title={__('Vaste lijst toevoegen', 'etm')} onClose={onClose}>
      <div class="etm-task-list">
        {templates.map((t) => (
          <div
            key={t.id}
            class="etm-task-list-item"
            role="button"
            tabIndex={pouring ? -1 : 0}
            aria-label={t.name}
            aria-disabled={pouring}
            onClick={() => handlePour(t.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePour(t.id);
              }
            }}
          >
            <div class="etm-task-list-item__body">
              <div class="etm-task-list-item__title">{t.name}</div>
              <div class="etm-task-list-item__meta">
                {__('%d items', 'etm').replace('%d', t.item_count)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/**
 * The per-item supermarket tag picker. Nine categories in the fixed walk
 * order plus a "Geen categorie" row that clears back to null — the
 * uncategorised state is chosen, not merely absent, so it needs a row of
 * its own rather than only being reachable by never tapping.
 */
function CategoryPickerModal({ item, onClose, onPick }) {
  const [saving, setSaving] = useState(false);

  function pick(value) {
    if (saving) return;
    setSaving(true);
    onPick(item, value);
  }

  const rows = [...SHOPLIST_CATEGORIES, SHOPLIST_UNCATEGORIZED];

  return (
    <Modal title={__('Categorie', 'etm')} onClose={onClose}>
      <div class="etm-quick-add-picker">
        {rows.map((cat) => {
          const active = (item.category || null) === cat.value;
          return (
            <button
              key={cat.value === null ? '__none__' : cat.value}
              type="button"
              class={`etm-quick-add-picker__item${active ? ' active' : ''}`}
              aria-pressed={active}
              disabled={saving}
              onClick={() => pick(cat.value)}
            >
              <Icon name={cat.value === null ? 'close' : cat.icon} size={18} />
              {cat.value === null ? __('Geen categorie', 'etm') : cat.label()}
              {active ? <Icon name="check" size={18} /> : null}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function ItemRow({ item, isTemplate, groupByCategory, onToggle, onDelete, onCategorize }) {
  // The tag button is suppressed on optimistic rows: their id is still a
  // client-side 'tmp-N', so the PATCH it would send could only 404. It
  // appears the moment the add is acked and the real id arrives. Checked
  // rows lose it too — tagging something already in the basket sorts
  // nothing (checked items sink to their own section either way).
  const canCategorize = groupByCategory && !item.checked_at && !item.temp;
  const info = item.category ? shoplistCategoryInfo(item.category) : null;

  return (
    <div class="etm-list-item">
      {!isTemplate ? (
        <input
          type="checkbox"
          class="etm-list-item__checkbox"
          checked={!!item.checked_at}
          aria-label={item.label}
          onChange={() => onToggle(item)}
        />
      ) : null}
      <div class="etm-list-item__body">
        <span class={`etm-list-item__label${item.checked_at ? ' etm-list-item__label--checked' : ''}`}>
          {item.label}
        </span>
        {item.note ? <span class="etm-list-item__note">{item.note}</span> : null}
      </div>
      {canCategorize ? (
        <button
          type="button"
          class="etm-icon-btn"
          // Icon is aria-hidden, so the current category has to travel in
          // the label or a screen reader hears only "Categorie: Melk".
          aria-label={`${__('Categorie', 'etm')}: ${item.label} — ${info ? info.label() : __('Geen categorie', 'etm')}`}
          onClick={() => onCategorize(item)}
        >
          <Icon name={info ? info.icon : 'sell'} size={18} />
        </button>
      ) : null}
      <button
        type="button"
        class="etm-icon-btn"
        aria-label={`${__('Verwijderen', 'etm')}: ${item.label}`}
        onClick={() => onDelete(item)}
      >
        <Icon name="delete" size={18} />
      </button>
    </div>
  );
}

/**
 * HH-5 rerouted this screen's items through the same per-list sync store
 * winkelmodus uses (lib/shoplist-sync.js): checkoff and add are now
 * optimistic-with-queue here too — one data path, so returning from
 * winkelmodus with unsynced checkoffs shows them instead of raw older
 * server state, and re-checking them here can't fork a second truth.
 * Structural actions (delete item/list, rename, pour) stay plain online
 * calls followed by a store refresh — they are deliberately not queue op
 * kinds (the brief limits the queue to checkoff/uncheck and add).
 */
export default function ListDetailScreen({ params }) {
  const listId = params?.listId;
  const store = getListStore(listId);

  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [showRename, setShowRename] = useState(false);
  const [showPour, setShowPour] = useState(false);
  const [categorizing, setCategorizing] = useState(null);

  useEffect(() => {
    let cancelled = false;

    refreshListStore(store).then((ok) => {
      if (!cancelled && !ok && !store.loaded.peek()) {
        showToast(__('Er ging iets mis. Probeer het opnieuw.', 'etm'), 'error');
      }
    });

    api.lists
      .list()
      .then((res) => {
        if (!cancelled) setTemplates((res.lists || []).filter((l) => l.is_template));
      })
      .catch((err) => {
        if (!cancelled) showToast(friendlyError(err), 'error');
      });

    // Fail-soft: without history the input still works, just suggestion-less.
    api.lists
      .itemHistory()
      .then((res) => {
        if (!cancelled) setHistory(res.labels || []);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [listId]);

  const list = store.meta.value;
  const items = store.state.value.items;
  const showSyncWarning = store.syncError.value && isDirty(store);

  function rememberInHistory(label) {
    setHistory((prev) => {
      const key = label.toLowerCase();
      if (prev.some((h) => h.toLowerCase() === key)) return prev;
      return [label, ...prev];
    });
  }

  function handleAdd(label) {
    addItem(store, label);
    rememberInHistory(label);
  }

  function handleToggle(item) {
    checkItem(store, item.id, !item.checked_at);
  }

  /**
   * Tagging is a STRUCTURAL action, deliberately not a queue op — the same
   * rule this screen's own docblock states for delete/rename/pour. The
   * brief limits the queue to checkoff/uncheck and add; a category is set
   * once, at a moment the user is standing still, and does not need to
   * survive a supermarket dead spot the way a checkoff does.
   *
   * A null pick clears, and the wire-level clear is the empty string: JSON
   * null is indistinguishable from an absent field through WP's
   * get_param(), so `''` is the convention ETM_Lists uses for `note` and
   * now `category` too. This is the one call site that has to know that.
   *
   * The follow-up refresh is safe next to the sync queue: mergeServerItems
   * re-overlays any queued local intent on top of whatever comes back, so
   * a checkoff still on the wire cannot be undone by this reload. No
   * bumpListsVersion() — the index screen's counts are unchanged by a tag.
   */
  async function handleCategorize(item, value) {
    setCategorizing(null);
    try {
      await api.lists.updateItem(listId, item.id, { category: value === null ? '' : value });
      await refreshListStore(store);
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  async function handleDeleteItem(item) {
    try {
      await api.lists.removeItem(listId, item.id);
      await refreshListStore(store);
      bumpListsVersion();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  async function handleDeleteList() {
    if (!confirm(__('Lijstje verwijderen?', 'etm'))) return;
    try {
      await api.lists.remove(listId);
      dropListStore(listId);
      bumpListsVersion();
      showToast(__('Lijstje verwijderd.', 'etm'), 'success');
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  async function handlePoured() {
    setShowPour(false);
    // refreshListStore swallows fetch errors by design (background polls
    // must stay silent) — but THIS refresh is the visible payoff of a
    // pour the user just saw succeed, so a failed reload deserves the
    // error hint HH-4 also gave here (review finding): without it the
    // pre-pour list sits on screen contradicting the success toast.
    const ok = await refreshListStore(store);
    if (!ok) {
      showToast(__('Vernieuwen is niet gelukt.', 'etm'), 'error');
    }
    bumpListsVersion();
  }

  if (!list) {
    return (
      <>
        <BackButton onClick={goBack} label={__('Boodschappen', 'etm')} />
        <Header title={__('Lijstje', 'etm')} />
      </>
    );
  }

  const unchecked = items.filter((i) => !i.checked_at);
  const checked = items.filter((i) => i.checked_at);

  return (
    <>
      <BackButton onClick={goBack} label={__('Boodschappen', 'etm')} />
      <Header
        title={list.name}
        actions={
          <button
            type="button"
            class="etm-icon-btn"
            aria-label={__('Lijstje bewerken', 'etm')}
            onClick={() => setShowRename(true)}
          >
            <Icon name="edit" size={20} />
          </button>
        }
      />

      {showSyncWarning ? (
        <p class="etm-sync-hint">
          <Icon name="cloud_off" size={16} /> {__('Niet gesynchroniseerd', 'etm')}
        </p>
      ) : null}

      {!list.is_template ? (
        <button type="button" class="etm-btn etm-btn-primary" onClick={() => navigate('shopping', { listId })}>
          <Icon name="shopping_cart" size={20} /> {__('Winkelmodus', 'etm')}
        </button>
      ) : null}

      <ItemAddInput history={history} onAdd={handleAdd} />

      {!list.is_template && templates.length > 0 ? (
        <button type="button" class="etm-btn etm-btn-secondary" onClick={() => setShowPour(true)}>
          <Icon name="content_copy" size={20} /> {__('Vaste lijst toevoegen', 'etm')}
        </button>
      ) : null}

      {items.length === 0 ? (
        <div class="etm-empty-state">{__('Nog geen items.', 'etm')}</div>
      ) : (
        <>
          <div class="etm-list-items">
            {unchecked.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                isTemplate={!!list.is_template}
                groupByCategory={!!list.group_by_category}
                onToggle={handleToggle}
                onDelete={handleDeleteItem}
                onCategorize={setCategorizing}
              />
            ))}
          </div>

          {!list.is_template && checked.length > 0 ? (
            <>
              <h3 class="etm-list-checked-header">
                {__('Afgevinkt', 'etm')} ({checked.length})
              </h3>
              <div class="etm-list-items">
                {checked.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    isTemplate={false}
                    groupByCategory={!!list.group_by_category}
                    onToggle={handleToggle}
                    onDelete={handleDeleteItem}
                    onCategorize={setCategorizing}
                  />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}

      <button type="button" class="etm-btn etm-btn-danger" onClick={handleDeleteList}>
        {__('Lijstje verwijderen', 'etm')}
      </button>

      {showRename ? (
        <ListEditModal
          editingList={list}
          onClose={() => setShowRename(false)}
          onSaved={(saved) => {
            setShowRename(false);
            // A type change rewrites what this screen renders (checkboxes,
            // winkelmodus, the pour button) and, for live → vaste lijst,
            // the server also cleared the checkoff state of the open rows.
            // Re-read rather than patch meta, so the items on screen match
            // the type they are now rendered under.
            if (!!saved.is_template !== !!list.is_template) {
              refreshListStore(store);
              return;
            }
            // A grouping-only flip needs no fetch at all: the flag gates
            // pure UI (the tag buttons, and winkelmodus's sort options),
            // and every item's own category is already on screen. Patching
            // the shared meta signal propagates it to both screens.
            store.meta.value = { ...list, name: saved.name, group_by_category: saved.group_by_category };
          }}
        />
      ) : null}

      {showPour ? (
        <PourTemplateModal
          listId={listId}
          templates={templates}
          onClose={() => setShowPour(false)}
          onPoured={handlePoured}
        />
      ) : null}

      {categorizing ? (
        <CategoryPickerModal
          item={categorizing}
          onClose={() => setCategorizing(null)}
          onPick={handleCategorize}
        />
      ) : null}
    </>
  );
}
